import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllUsers,
  deleteUser,
  updateUserInfo,
  updateUserStatus,
  resetPassword,
} from "../store/slices/authSlice";
import { fetchSocieties } from "../store/slices/societySlice";
import Switcher3 from "../components/Switcher3";
import {
  Phone,
  Mail,
  UserPlus,
  Edit2,
  Trash2,
  Search,
  User,
  IdCard,
  Upload,
  Eye,
  Building,
  Users,
  NotebookTabs,
  EyeOff,
  XCircle,
  Lock,
  PlusCircle,
  X,
} from "lucide-react";
import { useSidebarContext } from "../scenes/global/sidebar/SidebarContext";
import { Dialog } from "@headlessui/react";

const debounce = (func, delay) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), delay);
  };
};

const CustomerTable = () => {
  const dispatch = useDispatch();
  const { users, activeUser } = useSelector((state) => state.auth);
  const { societies } = useSelector((state) => state.societies);
  const { activeSociety, theme, gradient } = useSidebarContext();
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [updatedUsers, setUpdatedUsers] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [showIdModal, setShowIdModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState(null);
  const [inputErrors, setInputErrors] = useState({});
  const [isResetModalOpen, setResetModalOpen] = useState(false);
  const [selectedUserForReset, setSelectedUserForReset] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isWarisModalOpen, setWarisModalOpen] = useState(false);
  const [selectedWaris, setSelectedWaris] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [societyToAdd, setSocietyToAdd] = useState(null);
  const [selectedSocietyId, setSelectedSocietyId] = useState("");

  const handleAddSociety = (society) => {
    setSelectedCustomer((prev) => ({
      ...prev,
      societies: [...(prev.societies || []), society],
    }));
    setShowAddDialog(false);
    setSocietyToAdd(null);
    setSelectedSocietyId("");
  };

  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 6;

  const getSocietyById = (id) => {
    return societies.find((society) => society._id === id);
  };

  const openResetPasswordModal = (user) => {
    setSelectedUserForReset(user);
    setNewPassword("");
    setConfirmPassword("");
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setResetModalOpen(true);
  };

  const handleWarisClick = (warisInfo) => {
    setSelectedWaris(warisInfo);
    setWarisModalOpen(true);
  };

  const handleResetPassword = () => {
    if (!selectedUserForReset) {
      alert("No user selected for password reset!");
      return;
    }

    if (newPassword !== confirmPassword) {
      alert("Passwords do not match!");
      return;
    }

    dispatch(
      resetPassword({
        userId: selectedUserForReset._id,
        newPassword,
      })
    ).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        alert("Password reset successfully!");
        setResetModalOpen(false);
        setSelectedUserForReset(null);
      } else {
        alert("Password reset failed!");
      }
    });
  };

  const getPasswordStrength = (password) => {
    if (password.length < 6) return "Weak";
    if (password.length < 10) return "Medium";
    return "Strong";
  };

  const isEmployee = activeUser?.role === "Employee";
  useEffect(() => {
    dispatch(fetchAllUsers());
    dispatch(fetchSocieties());
  }, [dispatch]);

  useEffect(() => {
    setUpdatedUsers(users);
  }, [users]);

  const filteredUsers = users
    .filter((user) => user.role === "Customer")
    .filter((user) => {
      if (activeSociety && user.societies && Array.isArray(user.societies)) {
        return user.societies.some((s) => s._id === activeSociety._id);
      }
      return true;
    })
    .filter((user) => {
      const searchLower = searchQuery.toLowerCase();
      const name = user.customerData?.name?.toLowerCase() || "";
      const email = user.customerData?.contactInfo?.email?.toLowerCase() || "";
      const cnic = user.customerData?.contactInfo?.cnic?.toLowerCase() || "";
      return (
        name.includes(searchLower) ||
        email.includes(searchLower) ||
        cnic.includes(searchLower)
      );
    });

  const handleEdit = (user) => {
    if (isEmployee) return;
    setSelectedCustomer(user);
    setModalOpen(true);
  };

  const handleDelete = (userId) => {
    if (isEmployee) return;
    if (window.confirm("Are you sure you want to delete this customer?")) {
      // console.log('Deleting user with ID:', userId)
      dispatch(deleteUser(userId));
      setUpdatedUsers((prevUsers) =>
        prevUsers.filter((user) => user._id !== userId)
      );
      dispatch(fetchAllUsers());
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setIsUploading(true);
      setSelectedCustomer((prev) => ({
        ...prev,
        profile_picture_file: file,
        profile_picture: URL.createObjectURL(file),
      }));
      setPreviewImage(URL.createObjectURL(file));
      setIsUploading(false);
    }
  };

  const handleIdFrontImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setIsUploading(true);
      setSelectedCustomer((prev) => ({
        ...prev,
        customerData: {
          ...prev.customerData,
          contactInfo: {
            ...prev.customerData?.contactInfo,
            id_front_img_file: file,
            id_front_img: URL.createObjectURL(file),
          },
        },
      }));
      setIsUploading(false);
    }
  };

  const handleIdBackImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setIsUploading(true);
      setSelectedCustomer((prev) => ({
        ...prev,
        customerData: {
          ...prev.customerData,
          contactInfo: {
            ...prev.customerData?.contactInfo,
            id_back_img_file: file,
            id_back_img: URL.createObjectURL(file),
          },
        },
      }));
      setIsUploading(false);
    }
  };

  const handleWarisIdFrontImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setIsUploading(true);
      setSelectedCustomer((prev) => ({
        ...prev,
        customerData: {
          ...prev.customerData,
          contactInfo: {
            ...prev.customerData?.contactInfo,
            waris_id_front_img_file: file,
            waris_id_front_img: URL.createObjectURL(file),
          },
        },
      }));
      setIsUploading(false);
    }
  };

  const handleWarisIdBackImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setIsUploading(true);
      setSelectedCustomer((prev) => ({
        ...prev,
        customerData: {
          ...prev.customerData,
          contactInfo: {
            ...prev.customerData?.contactInfo,
            waris_id_back_img_file: file,
            waris_id_back_img: URL.createObjectURL(file),
          },
        },
      }));
      setIsUploading(false);
    }
  };

  const handleInputChange = useCallback((field) => {
    return (e) => {
      const { value } = e.target;

      // Check for leading/trailing spaces
      const hasLeadingSpace = value.length > 0 && value[0] === " ";
      const hasTrailingSpace =
        value.length > 0 && value[value.length - 1] === " ";

      // Update error state
      setInputErrors((prev) => {
        const newError =
          hasLeadingSpace || hasTrailingSpace
            ? "Leading or trailing spaces are not allowed."
            : "";
        return { ...prev, [field]: newError };
      });

      // Direct update without debounce for better responsiveness
      setSelectedCustomer((prev) => {
        if (field === "username") {
          return { ...prev, username: value };
        } else if (field === "name") {
          return {
            ...prev,
            customerData: { ...prev.customerData, name: value },
          };
        } else {
          return {
            ...prev,
            customerData: {
              ...prev.customerData,
              contactInfo: {
                ...prev.customerData.contactInfo,
                [field]: value,
              },
            },
          };
        }
      });
    };
  }, []);

  const inputHandlers = useMemo(() => {
    const handlers = {};
    [
      "username",
      "name",
      "cnic",
      "phone",
      "email",
      "father_name",
      "address",
      "waris_name",
      "waris_cnic",
      "waris_phone",
      "waris_relation",
      "waris_father_name",
      "waris_address",
    ].forEach((field) => {
      handlers[field] = handleInputChange(field);
    });
    return handlers;
  }, [handleInputChange]);

  const handleShowIds = (user, type) => {
    setSelectedIds({
      type,
      front:
        type === "customer"
          ? user.customerData?.contactInfo?.id_front_img
          : user.customerData?.contactInfo?.waris_id_front_img,
      back:
        type === "customer"
          ? user.customerData?.contactInfo?.id_back_img
          : user.customerData?.contactInfo?.waris_id_back_img,
      name:
        type === "customer"
          ? user.customerData?.name
          : user.customerData?.contactInfo?.waris_name,
    });
    setShowIdModal(true);
  };
  const IdModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-50 -mt-14"></div>
      <div
        className={`bg-gradient-to-br from-slate-600 via-slate-700 to-slate-600 p-8 rounded-3xl 
          border border-white/10 shadow-2xl max-w-3xl w-full`}
      >
        <h3 className={`text-2xl font-bold ${theme.tailwind.text9} mb-6`}>
          {selectedIds.type === "customer" ? "Customer" : "Waris"} ID Card -{" "}
          {selectedIds.name}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            { img: selectedIds.front, title: "Front Side" },
            { img: selectedIds.back, title: "Back Side" },
          ].map((side, index) => (
            <div key={index} className="space-y-2">
              <h4 className={`${theme.tailwind.text9} font-medium`}>
                {side.title}
              </h4>
              {side.img ? (
                <img
                  src={side.img}
                  alt={`${side.title} ID`}
                  className="w-full rounded-xl border border-white/10"
                />
              ) : (
                <div className="h-48 bg-white/5 rounded-xl flex items-center justify-center">
                  <span className={`${theme.tailwind.text2}`}>
                    No image available
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
        <button
          onClick={() => setShowIdModal(false)}
          className={`mt-6 w-full ${theme.tailwind.bgr19} ${theme.tailwind.text9} px-6 py-3 rounded-xl 
              font-semibold transform hover:scale-105 transition-all duration-300`}
        >
          Close
        </button>
      </div>
    </div>
  );

  const handleUpdate = () => {
    if (!selectedCustomer?._id) {
      console.error("User ID is missing, cannot update.");
      return;
    }

    // Create a FormData object instead of a regular JSON object
    const formData = new FormData();

    // Add basic user data
    formData.append("username", selectedCustomer.username);
    formData.append("role", "Customer");

    // Add arrays or objects as JSON strings
    if (selectedCustomer.societies) {
      formData.append("societies", JSON.stringify(selectedCustomer.societies));
    }
    if (selectedCustomer.status) {
      formData.append("status", selectedCustomer.status);
    }

    // Add profile picture if it's a file
    if (selectedCustomer.profile_picture_file) {
      formData.append("profile_picture", selectedCustomer.profile_picture_file);
    }

    // Add customer data
    if (selectedCustomer.customerData) {
      formData.append(
        "customerData[name]",
        selectedCustomer.customerData.name || ""
      );

      // Add contact info fields
      if (selectedCustomer.customerData.contactInfo) {
        const contactInfo = selectedCustomer.customerData.contactInfo;

        formData.append(
          "customerData[contactInfo][phone]",
          contactInfo.phone || ""
        );
        formData.append(
          "customerData[contactInfo][email]",
          contactInfo.email || ""
        );
        formData.append(
          "customerData[contactInfo][cnic]",
          contactInfo.cnic || ""
        );
        formData.append(
          "customerData[contactInfo][address]",
          contactInfo.address || ""
        );
        formData.append(
          "customerData[contactInfo][father_name]",
          contactInfo.father_name || ""
        );
        formData.append(
          "customerData[contactInfo][waris_address]",
          contactInfo.waris_address || ""
        );
        formData.append(
          "customerData[contactInfo][waris_name]",
          contactInfo.waris_name || ""
        );
        formData.append(
          "customerData[contactInfo][waris_relation]",
          contactInfo.waris_relation || ""
        );
        formData.append(
          "customerData[contactInfo][waris_cnic]",
          contactInfo.waris_cnic || ""
        );
        formData.append(
          "customerData[contactInfo][waris_phone]",
          contactInfo.waris_phone || ""
        );
        formData.append(
          "customerData[contactInfo][waris_father_name]",
          contactInfo.waris_father_name || ""
        );

        // Add image files
        if (contactInfo.id_front_img_file) {
          formData.append(
            "customerData[contactInfo][id_front_img]",
            contactInfo.id_front_img_file
          );
        }

        if (contactInfo.id_back_img_file) {
          formData.append(
            "customerData[contactInfo][id_back_img]",
            contactInfo.id_back_img_file
          );
        }

        if (contactInfo.waris_id_front_img_file) {
          formData.append(
            "customerData[contactInfo][waris_id_front_img]",
            contactInfo.waris_id_front_img_file
          );
        }

        if (contactInfo.waris_id_back_img_file) {
          formData.append(
            "customerData[contactInfo][waris_id_back_img]",
            contactInfo.waris_id_back_img_file
          );
        }
      }
    }

    dispatch(
      updateUserInfo({ userId: selectedCustomer._id, updatedData: formData })
    )
      .then((res) => {
        // console.log('Update response:', res)

        if (res.meta.requestStatus === "fulfilled") {
          // console.log('Update successful, fetching users...')
          dispatch(fetchAllUsers()).then((res) => {
            setUpdatedUsers(res.payload);
          });
          setModalOpen(false);
        } else {
          console.error("Update failed:", res.error);
        }
      })
      .catch((error) => {
        console.error("Error updating user:", error);
      });
  };

  // Add these at the top with other memoized functions
  const cleanProfilePictureURL = React.useCallback((url) => {
    if (!url) return null;
    if (url.includes("//uploads")) {
      return url.replace("//uploads", "/uploads");
    }
    return url;
  }, []);

  const formatCNIC = React.useCallback((cnic) => {
    if (!cnic) return "";
    const cleaned = cnic.replace(/\D/g, "").slice(0, 13);
    return cleaned.replace(/^(\d{5})(\d{7})(\d{1})$/, "$1-$2-$3");
  }, []);

  const formatPhoneNumber = React.useCallback((phone) => {
    if (!phone) return "";
    const cleanedPhone = phone.replace(/\D/g, "");
    if (cleanedPhone.length === 11 && cleanedPhone.startsWith("92")) {
      return `+92 ${cleanedPhone.substring(2, 5)}-${cleanedPhone.substring(5)}`;
    }
    return phone;
  }, []);

  const handleStatusChange = (userId, newStatus) => {
    const updatedData = { status: newStatus };
    // console.log('Sending status update:', { userId, updatedData })
    if (isEmployee) return;
    dispatch(
      updateUserStatus({ userId, status: newStatus ? "active" : "inactive" })
    )
      .then((res) => {
        if (res.meta.requestStatus === "fulfilled") {
          dispatch(fetchAllUsers());
          // console.log('Status updated successfully for user ID:', userId)
        } else {
          console.error("Status update failed:", res.error);
        }
      })
      .catch((error) => {
        console.error("Error updating user status:", error);
      });
  };

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstRow, indexOfLastRow);

  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    const renderPageNumbers = () => {
      const pageNumbers = [];
      const showEllipsis = totalPages > 7;

      if (showEllipsis) {
        if (currentPage <= 4) {
          // Show first 5 pages, ellipsis, and last page
          for (let i = 1; i <= 5; i++) {
            pageNumbers.push(i);
          }
          pageNumbers.push("...");
          pageNumbers.push(totalPages);
        } else if (currentPage >= totalPages - 3) {
          // Show first page, ellipsis, and last 5 pages
          pageNumbers.push(1);
          pageNumbers.push("...");
          for (let i = totalPages - 4; i <= totalPages; i++) {
            pageNumbers.push(i);
          }
        } else {
          // Show first page, ellipsis, current page and neighbors, ellipsis, last page
          pageNumbers.push(1);
          pageNumbers.push("...");
          for (let i = currentPage - 1; i <= currentPage + 1; i++) {
            pageNumbers.push(i);
          }
          pageNumbers.push("...");
          pageNumbers.push(totalPages);
        }
      } else {
        // Show all pages if total pages <= 7
        for (let i = 1; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      }

      return pageNumbers;
    };

    return (
      <div className="flex justify-center items-center space-x-2 mt-8 mb-4">
        {/* Previous Button */}
        <button
          onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`px-3 py-2 rounded-xl backdrop-blur-sm transition-all duration-300
            ${
              currentPage === 1
                ? `${theme.tailwind.text21} bg-teal-300/5`
                : `${theme.tailwind.text9} ${theme.tailwind.bg11} ${theme.tailwind.bgHover26} hover:scale-105`
            }`}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        {/* Page Numbers */}
        <div className="flex items-center space-x-2">
          {renderPageNumbers().map((pageNum, idx) =>
            pageNum === "..." ? (
              <span
                key={`ellipsis-${idx}`}
                className={`${theme.tailwind.text20}`}
              >
                ...
              </span>
            ) : (
              <button
                key={pageNum}
                onClick={() => onPageChange(pageNum)}
                className={`min-w-[36px] h-8 rounded-xl backdrop-blur-sm transition-all duration-300
                  ${
                    currentPage === pageNum
                      ? `${theme.tailwind.bgr28} ${theme.tailwind.text18} font-semibold scale-110 ring-2 ${theme.tailwind.ringColor4}`
                      : `${theme.tailwind.bg11} ${theme.tailwind.text9} ${theme.tailwind.bgHover26} hover:scale-105`
                  }`}
              >
                {pageNum}
              </button>
            )
          )}
        </div>

        {/* Next Button */}
        <button
          onClick={() =>
            currentPage < totalPages && onPageChange(currentPage + 1)
          }
          disabled={currentPage === totalPages}
          className={`px-3 py-2 rounded-xl backdrop-blur-sm transition-all duration-300
            ${
              currentPage === totalPages
                ? `${theme.tailwind.text21} bg-teal-300/5`
                : `${theme.tailwind.text9} ${theme.tailwind.bg11} ${theme.tailwind.bgHover26} hover:scale-105`
            }`}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>
    );
  };

  const handleDeleteImage = () => {
    setSelectedCustomer((prev) => ({
      ...prev,
      profile_picture: null,
    }));
    setPreviewImage(null);
  };

  return (
    <div
      className={`${gradient} rounded-2xl shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] p-6 animate-gradient-x`}
      style={{
        backgroundImage: `
          linear-gradient(to bottom right, ${theme.mui.bg2}),
          url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100%25' height='100%25'%3E%3Crect fill='%231A1F2E' width='540' height='450'/%3E%3Cdefs%3E%3ClinearGradient id='a' gradientUnits='userSpaceOnUse' x1='0' x2='0' y1='0' y2='100%25' gradientTransform='rotate(240)'%3E%3Cstop offset='0' stop-color='%231A1F2E'/%3E%3Cstop offset='1' stop-color='%23373F5A'/%3E%3C/linearGradient%3E%3Cpattern patternUnits='userSpaceOnUse' id='b' width='300' height='250' x='0' y='0' viewBox='0 0 1080 900'%3E%3Cg fill-opacity='0.04'%3E%3Cpolygon fill='%23444' points='90 150 0 300 180 300'/%3E%3Cpolygon points='90 150 180 0 0 0'/%3E%3Cpolygon fill='%23AAA' points='270 150 360 0 180 0'/%3E%3Cpolygon fill='%23DDD' points='450 150 360 300 540 300'/%3E%3Cpolygon fill='%23999' points='450 150 540 0 360 0'/%3E%3Cpolygon points='630 150 540 300 720 300'/%3E%3Cpolygon fill='%23DDD' points='630 150 720 0 540 0'/%3E%3Cpolygon fill='%23444' points='810 150 720 300 900 300'/%3E%3Cpolygon fill='%23FFF' points='810 150 900 0 720 0'/%3E%3Cpolygon fill='%23DDD' points='990 150 900 300 1080 300'/%3E%3Cpolygon fill='%23444' points='990 150 1080 0 900 0'/%3E%3Cpolygon fill='%23DDD' points='90 450 0 600 180 600'/%3E%3Cpolygon points='90 450 180 300 0 300'/%3E%3Cpolygon fill='%23666' points='270 450 180 600 360 600'/%3E%3Cpolygon fill='%23AAA' points='270 450 360 300 180 300'/%3E%3Cpolygon fill='%23DDD' points='450 450 360 600 540 600'/%3E%3Cpolygon fill='%23999' points='450 450 540 300 360 300'/%3E%3Cpolygon fill='%23999' points='630 450 540 600 720 600'/%3E%3Cpolygon fill='%23FFF' points='630 450 720 300 540 300'/%3E%3Cpolygon points='810 450 720 600 900 600'/%3E%3Cpolygon fill='%23DDD' points='810 450 900 300 720 300'/%3E%3Cpolygon fill='%23AAA' points='990 450 900 600 1080 600'/%3E%3Cpolygon fill='%23444' points='990 450 1080 300 900 300'/%3E%3Cpolygon fill='%23222' points='90 750 0 900 180 900'/%3E%3Cpolygon points='270 750 180 900 360 900'/%3E%3Cpolygon fill='%23DDD' points='270 750 360 600 180 600'/%3E%3Cpolygon points='450 750 540 600 360 600'/%3E%3Cpolygon points='630 750 540 900 720 900'/%3E%3Cpolygon fill='%23444' points='630 750 720 600 540 600'/%3E%3Cpolygon fill='%23AAA' points='810 750 720 900 900 900'/%3E%3Cpolygon fill='%23666' points='810 750 900 600 720 600'/%3E%3Cpolygon fill='%23999' points='990 750 900 900 1080 900'/%3E%3Cpolygon fill='%23999' points='180 0 90 150 270 150'/%3E%3Cpolygon fill='%23444' points='360 0 270 150 450 150'/%3E%3Cpolygon fill='%23FFF' points='540 0 450 150 630 150'/%3E%3Cpolygon points='900 0 810 150 990 150'/%3E%3Cpolygon fill='%23222' points='0 300 -90 450 90 450'/%3E%3Cpolygon fill='%23FFF' points='0 300 90 150 -90 150'/%3E%3Cpolygon fill='%23FFF' points='180 300 90 450 270 450'/%3E%3Cpolygon fill='%23666' points='180 300 270 150 90 150'/%3E%3Cpolygon fill='%23222' points='360 300 270 450 450 450'/%3E%3Cpolygon fill='%23FFF' points='360 300 450 150 270 150'/%3E%3Cpolygon fill='%23444' points='540 300 450 450 630 450'/%3E%3Cpolygon fill='%23222' points='540 300 630 150 450 150'/%3E%3Cpolygon fill='%23AAA' points='720 300 630 450 810 450'/%3E%3Cpolygon fill='%23666' points='720 300 810 150 630 150'/%3E%3Cpolygon fill='%23FFF' points='900 300 810 450 990 450'/%3E%3Cpolygon fill='%23999' points='900 300 990 150 810 150'/%3E%3Cpolygon points='0 600 -90 750 90 750'/%3E%3Cpolygon fill='%23666' points='0 600 90 450 -90 450'/%3E%3Cpolygon fill='%23AAA' points='180 600 90 750 270 750'/%3E%3Cpolygon fill='%23444' points='180 600 270 450 90 450'/%3E%3Cpolygon fill='%23444' points='360 600 270 750 450 750'/%3E%3Cpolygon fill='%23999' points='360 600 450 450 270 450'/%3E%3Cpolygon fill='%23666' points='540 600 630 450 450 450'/%3E%3Cpolygon fill='%23222' points='720 600 630 750 810 750'/%3E%3Cpolygon fill='%23FFF' points='900 600 810 750 990 750'/%3E%3Cpolygon fill='%23222' points='900 600 990 450 810 450'/%3E%3Cpolygon fill='%23DDD' points='0 900 90 750 -90 750'/%3E%3Cpolygon fill='%23444' points='180 900 270 750 90 750'/%3E%3Cpolygon fill='%23FFF' points='360 900 450 750 270 750'/%3E%3Cpolygon fill='%23AAA' points='540 900 630 750 450 750'/%3E%3Cpolygon fill='%23FFF' points='720 900 810 750 630 750'/%3E%3Cpolygon fill='%23222' points='900 900 990 750 810 750'/%3E%3Cpolygon fill='%23222' points='1080 300 990 450 1170 450'/%3E%3Cpolygon fill='%23FFF' points='1080 300 1170 150 990 150'/%3E%3Cpolygon points='1080 600 990 750 1170 750'/%3E%3Cpolygon fill='%23666' points='1080 600 1170 450 990 450'/%3E%3Cpolygon fill='%23DDD' points='1080 900 1170 750 990 750'/%3E%3C/g%3E%3C/pattern%3E%3C/defs%3E%3Crect x='0' y='0' fill='url(%23a)' width='100%25' height='100%25'/%3E%3Crect x='0' y='0' fill='url(%23b)' width='100%25' height='100%25'/%3E%3C/svg%3E")
        `,
      }}
    >
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Enhanced Header Section */}
        <div className="relative group p-6 rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 hover:border-white/20 transition-all duration-500">
          <div className="flex flex-col space-y-3">
            <h1
              className={`text-4xl font-bold tracking-tight ${theme.tailwind.bgr6} text-transparent bg-clip-text transform group-hover:scale-[1.02] transition-all duration-500`}
            >
              Customer Management
            </h1>
            <div
              className={`flex items-center space-x-2 ${theme.tailwind.text4}`}
            >
              <Building className="h-5 w-5" />
              <span>{activeSociety?.name || "No Society Selected"}</span>
            </div>
            <div className="flex items-center space-x-2 bg-white/5 px-4 py-2 rounded-xl">
              <Users className={`h-5 w-5 ${theme.tailwind.text9}`} />
              <span className={`${theme.tailwind.text9} font-medium`}>
                {filteredUsers.length} Customers
              </span>
            </div>
            <p
              className={`${theme.tailwind.text4} text-lg transform group-hover:translate-x-2 transition-all duration-500`}
            >
              Manage and monitor your customer base efficiently
            </p>
          </div>
          <div
            className={`absolute inset-0 rounded-2xl ${theme.tailwind.bgr20} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
          />
        </div>

        {/* Enhanced Search Bar */}
        <div className="relative group transform hover:-translate-y-1 transition-all duration-500">
          <div
            className={`absolute inset-0 rounded-xl ${theme.tailwind.bgr21} blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500`}
          />
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <Search
              className={`h-6 w-6 ${theme.tailwind.text9} group-hover:rotate-12 transition-transform duration-500`}
            />
          </div>
          <input
            type="text"
            autoComplete="off"
            placeholder="Search users by name, email, or CNIC..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`w-full pl-12 pr-4 py-4 rounded-xl bg-white/5 border border-white/10 ${theme.tailwind.text9} 
            ${theme.tailwind.placeHolder} focus:outline-none focus:ring-2 ${theme.tailwind.focusRing6} 
            ${theme.tailwind.focus7} transition-all duration-500 backdrop-blur-xl
            hover:bg-white/10 hover:shadow-lg ${theme.tailwind.shadowhover6}`}
          />
        </div>

        <div>
          <div className="relative overflow-hidden rounded-2xl backdrop-blur-2xl">
            {/* Gradient background effect */}
            <div
              className={`absolute inset-0 ${theme.tailwind.bgGradient16}`}
            />

            <div className="relative overflow-x-auto">
              <table className="max-w-7xl">
                <thead>
                  <tr className={`border-b ${theme.tailwind.border1}`}>
                    {[
                      "User Details",
                      "Contact Info",
                      "Plots",
                      "Id Card Preview",
                      "Status",
                      !isEmployee && "Actions",
                      !isEmployee && "Reset Password",
                    ]
                      .filter(Boolean)
                      .map((header) => (
                        <th key={header} className="px-6 py-5 text-left">
                          <span
                            className={`text-sm font-semibold bg-clip-text text-transparent ${theme.tailwind.bgr22}`}
                          >
                            {header}
                          </span>
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {currentUsers.map((user) => (
                    <tr
                      key={user._id}
                      onMouseEnter={() => setHoveredRow(user._id)}
                      onMouseLeave={() => setHoveredRow(null)}
                      className={`relative group border-b ${theme.tailwind.border23} ${theme.tailwind.groupHoverBgr}`}
                    >
                      <td className="px-6 py-5">
                        <div className="flex items-start space-x-4">
                          <div className="relative">
                            {user.profile_picture ? (
                              <img
                                src={cleanProfilePictureURL(
                                  user.profile_picture
                                )}
                                alt={user.customerData?.name}
                                className={`w-10 h-10 rounded-2xl object-cover ring-4 ${theme.tailwind.ringColor4} ${theme.tailwind.ringHoverG} 
                          transform group-hover:scale-110 transition-all duration-500`}
                              />
                            ) : (
                              <div
                                className={`w-10 h-10 rounded-2xl ${theme.tailwind.bgGradient17} ring-4 
                          ${theme.tailwind.ringColor4} ${theme.tailwind.ringHoverG} flex items-center justify-center transform 
                          group-hover:scale-110 transition-all duration-500`}
                              >
                                <User
                                  className={`h-8 w-8 ${theme.tailwind.text9}`}
                                />
                              </div>
                            )}
                            <div
                              className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full ${theme.tailwind.bgGradient18} 
                        ring-2 ${theme.tailwind.ringColor5} group-hover:scale-125 transition-all duration-500`}
                            />
                          </div>
                          <div className="flex-1 space-y-2">
                            <div className="transform group-hover:translate-x-2 transition-all duration-500">
                              <h3 className={`${theme.tailwind.text9} text-sm`}>
                                <spam
                                  className={`text-sm ${theme.tailwind.text20}`}
                                >
                                  Name:{" "}
                                </spam>
                                {user.customerData?.name || "N/A"}
                              </h3>
                              <p className={`${theme.tailwind.text9} text-sm`}>
                                <spam
                                  className={`text-sm ${theme.tailwind.text20}`}
                                >
                                  Username:{" "}
                                </spam>
                                {user.username}
                              </p>
                            </div>
                            {!isEmployee && (
                              <div
                                className={`inline-flex items-center gap-2 px-2 py-1 rounded-full ${theme.tailwind.bgGradient18} ring-2 ${theme.tailwind.ringColor5} group hover:scale-105 transition-all duration-500`}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="mt-1.5">******</span>
                                  <Lock
                                    className={`w-4 h-4 ${theme.tailwind.text18}`}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Contact Info Column */}
                      <td className="px-6 py-5">
                        <div className="space-y-3">
                          {[
                            {
                              icon: Phone,
                              value: user.customerData?.contactInfo?.phone,
                            },
                            {
                              icon: Mail,
                              value: user.customerData?.contactInfo?.email,
                            },
                            {
                              icon: NotebookTabs,
                              value: user.customerData?.contactInfo?.address,
                              isAddress: true,
                            },
                            {
                              icon: IdCard,
                              value: user.customerData?.contactInfo?.cnic,
                            },
                          ].map((item, idx) => (
                            <div
                              key={idx}
                              className={`flex items-center gap-2 ${theme.tailwind.text20} ${theme.tailwind.groupHover7} transition-colors duration-300`}
                            >
                              <item.icon className="h-4 w-4" />
                              <span
                                className={`text-sm ${
                                  item.isAddress ? "break-words w-[200px]" : ""
                                }`}
                              >
                                {item.value || "N/A"}
                              </span>
                            </div>
                          ))}

                          {/* Waris Info Button */}
                          <button
                            onClick={() =>
                              handleWarisClick(user.customerData?.contactInfo)
                            }
                            className={`mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-xl ${theme.tailwind.bgr23} 
          ${theme.tailwind.groupHoverG6} ${theme.tailwind.text9} transition-all duration-300 transform hover:scale-105`}
                          >
                            <UserPlus className="h-4 w-4" />
                            <span>View Waris Info</span>
                          </button>
                        </div>
                      </td>

                      {/* Plots Column */}
                      <td className="px-6 py-5">
                        <div
                          className={`px-4 py-2 rounded-lg ${theme.tailwind.bg11} ${theme.tailwind.text9} text-sm inline-block`}
                        >
                          {user.customerData?.plots?.length || 0} plot
                          {user.customerData?.plots?.length > 1 ? "s" : ""}
                        </div>
                      </td>

                      {/* ID Preview Column */}
                      <td className="px-6 py-5">
                        <div className="space-y-3">
                          {[
                            { label: "User Id", type: "customer" },
                            { label: "Waris Id", type: "waris" },
                          ].map((item) => (
                            <button
                              key={item.type}
                              onClick={() => handleShowIds(user, item.type)}
                              className={`w-full flex items-center justify-between p-2 rounded-lg ${theme.tailwind.bgr23} ${theme.tailwind.groupHoverG6} ${theme.tailwind.text9} transition-all duration-300`}
                            >
                              <span className="text-sm">{item.label}</span>
                              <Eye className="h-4 w-4" />
                            </button>
                          ))}
                        </div>
                      </td>

                      {/* Status Column */}
                      <td className="px-6 py-5">
                        {isEmployee ? (
                          <span
                            className={`px-3 py-1 rounded-full text-sm font-medium ${
                              user.status === "active"
                                ? "bg-green-400/10 text-green-400"
                                : "bg-red-400/10 text-red-400"
                            }`}
                          >
                            {user.status === "active" ? "Active" : "Inactive"}
                          </span>
                        ) : (
                          <Switcher3
                            isChecked={user.status === "active"}
                            onToggle={(newStatus) =>
                              handleStatusChange(user._id, newStatus)
                            }
                          />
                        )}
                      </td>

                      {/* Actions Column */}
                      {!isEmployee && (
                        <td className="px-6 py-5">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => handleEdit(user)}
                              className={`p-2 rounded-lg ${theme.tailwind.bg11} ${theme.tailwind.bgHover26} ${theme.tailwind.text9} 
                            transform hover:scale-110 transition-all duration-300`}
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(user._id)}
                              className="p-2 rounded-lg bg-red-400/10 hover:bg-red-400/20 text-red-400 
                            transform hover:scale-110 transition-all duration-300"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      )}
                      {!isEmployee && (
                        <td className="p-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevents event bubbling
                              openResetPasswordModal(user);
                            }}
                            className={`bg-red-500 hover:bg-red-600 ${theme.tailwind.text9} px-2 py-1 rounded`}
                          >
                            Reset Password
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {filteredUsers.length > rowsPerPage && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}

        {showIdModal && <IdModal />}

        {isModalOpen && selectedCustomer && (
          <div className="fixed inset-0 z-[9999]">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/60 backdrop-blur-sm"
              style={{ top: 0, left: 0, right: 0, bottom: 0 }}
              onClick={() => setModalOpen(false)}
            />

            {/* Modal Container - SCROLLABLE */}
            <div className="fixed inset-0 overflow-y-auto">
              <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8 flex items-start justify-center">
                <div className="relative w-full max-w-6xl my-6">
                  {/* Subtle Background Glow */}
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-teal-500/20 to-slate-600/20 rounded-2xl blur-xl"></div>

                  {/* Main Modal */}
                  <div className="relative bg-gradient-to-br from-slate-800/95 via-slate-900/95 to-slate-800/95 backdrop-blur-2xl rounded-2xl border border-slate-700/50 shadow-2xl overflow-hidden">
                    {/* Top Border */}
                    <div className="h-1 bg-gradient-to-r from-teal-400 via-teal-500 to-teal-600"></div>

                    <div className="relative p-6 space-y-6">
                      {/* Header - SMALLER */}
                      <div className="flex items-center justify-between pb-4 border-b border-slate-700/50">
                        <div className="space-y-1">
                          <h2 className="text-2xl font-bold bg-gradient-to-r from-teal-400 to-slate-300 text-transparent bg-clip-text">
                            Edit Customer Profile
                          </h2>
                          <p className="text-slate-400 text-sm">
                            Update customer information and documents
                          </p>
                        </div>
                        <button
                          onClick={() => setModalOpen(false)}
                          className="p-2 rounded-xl bg-slate-700/50 hover:bg-red-500/20 text-slate-400 hover:text-red-400 transition-all duration-300 hover:rotate-90"
                        >
                          <XCircle className="w-5 h-5" />
                        </button>
                      </div>

                      {/* Profile Picture - SMALLER */}
                      <div className="flex justify-center py-4">
                        <div className="relative group">
                          <div className="absolute -inset-2 bg-gradient-to-r from-teal-500/30 to-slate-600/30 rounded-2xl blur-md opacity-0 group-hover:opacity-100 transition-all duration-500"></div>

                          <div className="relative w-32 h-32 rounded-2xl overflow-hidden ring-2 ring-teal-500/40 shadow-xl">
                            <img
                              src={
                                previewImage ||
                                cleanProfilePictureURL(
                                  selectedCustomer.profile_picture
                                ) ||
                                "/api/placeholder/128/128"
                              }
                              alt="Profile"
                              className="w-full h-full object-cover transform group-hover:scale-110 transition-all duration-500"
                            />

                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center gap-3">
                              <label className="cursor-pointer p-2.5 rounded-xl bg-teal-500/30 hover:bg-teal-500/50 backdrop-blur-sm border border-teal-400/30 transition-all duration-300 hover:scale-110">
                                <input
                                  type="file"
                                  className="hidden"
                                  accept="image/*"
                                  onChange={handleImageChange}
                                />
                                <Upload className="h-4 w-4 text-teal-300" />
                              </label>

                              {selectedCustomer.profile_picture && (
                                <button
                                  onClick={handleDeleteImage}
                                  className="p-2.5 rounded-xl bg-red-500/30 hover:bg-red-500/50 backdrop-blur-sm border border-red-400/30 transition-all duration-300 hover:scale-110"
                                >
                                  <Trash2 className="h-4 w-4 text-red-300" />
                                </button>
                              )}
                            </div>
                          </div>

                          <div className="absolute -bottom-1 -right-1 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-teal-500/20 backdrop-blur-sm border border-teal-400/30">
                            <div className="w-2 h-2 rounded-full bg-teal-400 animate-pulse"></div>
                            <span className="text-teal-300 font-medium text-xs">
                              Active
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Two Column Layout */}
                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                        {/* Personal Details */}
                        <div className="space-y-4 p-5 rounded-xl bg-slate-800/40 backdrop-blur-xl border border-slate-700/50 hover:border-teal-500/30 transition-all duration-500">
                          <div className="flex items-center gap-2 mb-3">
                            <div className="p-2 rounded-lg bg-teal-500/20">
                              <User className="h-4 w-4 text-teal-400" />
                            </div>
                            <h3 className="text-lg font-bold text-white">
                              Personal Details
                            </h3>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {[
                              {
                                icon: User,
                                placeholder: "Username",
                                value: selectedCustomer.username,
                                field: "username",
                              },
                              {
                                icon: User,
                                placeholder: "Full Name",
                                value: selectedCustomer.customerData?.name,
                                field: "name",
                              },
                              {
                                icon: IdCard,
                                placeholder: "CNIC",
                                value:
                                  selectedCustomer.customerData?.contactInfo
                                    ?.cnic,
                                field: "cnic",
                                format: formatCNIC,
                              },
                              {
                                icon: Phone,
                                placeholder: "Phone Number",
                                value:
                                  selectedCustomer.customerData?.contactInfo
                                    ?.phone,
                                field: "phone",
                                format: formatPhoneNumber,
                              },
                              {
                                icon: Mail,
                                placeholder: "Email Address",
                                value:
                                  selectedCustomer.customerData?.contactInfo
                                    ?.email,
                                field: "email",
                              },
                              {
                                icon: User,
                                placeholder: "Father/Husband Name",
                                value:
                                  selectedCustomer.customerData?.contactInfo
                                    ?.father_name,
                                field: "father_name",
                              },
                            ].map((field, idx) => (
                              <div key={idx} className="relative group">
                                <field.icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500 group-hover:text-teal-400 transition-colors duration-300 z-10" />
                                <input
                                  type="text"
                                  placeholder={field.placeholder}
                                  value={
                                    field.format
                                      ? field.format(field.value || "")
                                      : field.value || ""
                                  }
                                  onChange={inputHandlers[field.field]}
                                  className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-slate-900/60 border border-slate-700 text-white text-sm placeholder-slate-500 
                          focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 hover:border-slate-600
                          transition-all duration-300 backdrop-blur-sm"
                                />
                                {field.field === "username" &&
                                  inputErrors[field.field] && (
                                    <p className="text-red-400 text-xs mt-1">
                                      {inputErrors[field.field]}
                                    </p>
                                  )}
                              </div>
                            ))}

                            <div className="md:col-span-2 relative group">
                              <NotebookTabs className="absolute left-3 top-3 h-4 w-4 text-slate-500 group-hover:text-teal-400 transition-colors duration-300 z-10" />
                              <textarea
                                placeholder="Full Address"
                                value={
                                  selectedCustomer.customerData?.contactInfo
                                    ?.address || ""
                                }
                                onChange={(e) =>
                                  handleInputChange(e, "address")
                                }
                                rows="2"
                                className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-slate-900/60 border border-slate-700 text-white text-sm placeholder-slate-500 
                        focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 hover:border-slate-600
                        transition-all duration-300 backdrop-blur-sm resize-none"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Waris Details */}
                        <div className="space-y-4 p-5 rounded-xl bg-slate-800/40 backdrop-blur-xl border border-slate-700/50 hover:border-teal-500/30 transition-all duration-500">
                          <div className="flex items-center gap-2 mb-3">
                            <div className="p-2 rounded-lg bg-slate-600/30">
                              <UserPlus className="h-4 w-4 text-slate-300" />
                            </div>
                            <h3 className="text-lg font-bold text-white">
                              Waris Details
                            </h3>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {[
                              {
                                icon: UserPlus,
                                placeholder: "Waris Name",
                                value:
                                  selectedCustomer.customerData?.contactInfo
                                    ?.waris_name,
                                field: "waris_name",
                              },
                              {
                                icon: IdCard,
                                placeholder: "Waris CNIC",
                                value:
                                  selectedCustomer.customerData?.contactInfo
                                    ?.waris_cnic,
                                field: "waris_cnic",
                                format: formatCNIC,
                              },
                              {
                                icon: Phone,
                                placeholder: "Waris Phone",
                                value:
                                  selectedCustomer.customerData?.contactInfo
                                    ?.waris_phone,
                                field: "waris_phone",
                                format: formatPhoneNumber,
                              },
                              {
                                icon: User,
                                placeholder: "Relation",
                                value:
                                  selectedCustomer.customerData?.contactInfo
                                    ?.waris_relation,
                                field: "waris_relation",
                              },
                              {
                                icon: User,
                                placeholder: "Waris Father Name",
                                value:
                                  selectedCustomer.customerData?.contactInfo
                                    ?.waris_father_name,
                                field: "waris_father_name",
                              },
                            ].map((field, idx) => (
                              <div key={idx} className="relative group">
                                <field.icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500 group-hover:text-slate-300 transition-colors duration-300 z-10" />
                                <input
                                  type="text"
                                  placeholder={field.placeholder}
                                  value={
                                    field.format
                                      ? field.format(field.value || "")
                                      : field.value || ""
                                  }
                                  onChange={inputHandlers[field.field]}
                                  className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-slate-900/60 border border-slate-700 text-white text-sm placeholder-slate-500 
                          focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500/20 hover:border-slate-600
                          transition-all duration-300 backdrop-blur-sm"
                                />
                              </div>
                            ))}

                            <div className="md:col-span-2 relative group">
                              <NotebookTabs className="absolute left-3 top-3 h-4 w-4 text-slate-500 group-hover:text-slate-300 transition-colors duration-300 z-10" />
                              <textarea
                                placeholder="Waris Full Address"
                                value={
                                  selectedCustomer.customerData?.contactInfo
                                    ?.waris_address || ""
                                }
                                onChange={(e) =>
                                  handleInputChange(e, "waris_address")
                                }
                                rows="2"
                                className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-slate-900/60 border border-slate-700 text-white text-sm placeholder-slate-500 
                        focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500/20 hover:border-slate-600
                        transition-all duration-300 backdrop-blur-sm resize-none"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* ID Documents - SMALLER */}
                      <div className="space-y-4 p-5 rounded-xl bg-slate-800/40 backdrop-blur-xl border border-slate-700/50">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="p-2 rounded-lg bg-teal-500/20">
                            <IdCard className="h-4 w-4 text-teal-400" />
                          </div>
                          <h3 className="text-lg font-bold text-white">
                            Identity Documents
                          </h3>
                        </div>

                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                          {[
                            {
                              title: "ID Front",
                              src: selectedCustomer.customerData?.contactInfo
                                ?.id_front_img,
                              handler: handleIdFrontImageChange,
                            },
                            {
                              title: "ID Back",
                              src: selectedCustomer.customerData?.contactInfo
                                ?.id_back_img,
                              handler: handleIdBackImageChange,
                            },
                            {
                              title: "Waris ID Front",
                              src: selectedCustomer.customerData?.contactInfo
                                ?.waris_id_front_img,
                              handler: handleWarisIdFrontImageChange,
                            },
                            {
                              title: "Waris ID Back",
                              src: selectedCustomer.customerData?.contactInfo
                                ?.waris_id_back_img,
                              handler: handleWarisIdBackImageChange,
                            },
                          ].map((doc, idx) => (
                            <div key={idx} className="relative group">
                              <div className="absolute -inset-0.5 bg-gradient-to-r from-teal-500/20 to-slate-600/20 rounded-xl blur opacity-0 group-hover:opacity-100 transition-all duration-500"></div>

                              <div className="relative">
                                <div className="aspect-[3/4] rounded-xl overflow-hidden border border-dashed border-teal-500/30 group-hover:border-teal-500/60 transition-all duration-300">
                                  <img
                                    src={doc.src || "/api/placeholder/150/200"}
                                    alt={doc.title}
                                    className="w-full h-full object-cover transform group-hover:scale-105 transition-all duration-500"
                                  />

                                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center">
                                    <label className="cursor-pointer p-3 rounded-xl bg-teal-500/30 hover:bg-teal-500/50 backdrop-blur-sm border border-teal-400/30 transition-all duration-300 hover:scale-110">
                                      <input
                                        type="file"
                                        className="hidden"
                                        accept="image/*"
                                        onChange={doc.handler}
                                      />
                                      <Upload className="h-4 w-4 text-teal-300" />
                                    </label>
                                  </div>
                                </div>

                                <div className="mt-2 text-center">
                                  <p className="text-slate-300 font-medium text-xs">
                                    {doc.title}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Society Management - SMALLER */}
                      <div className="space-y-4 p-5 rounded-xl bg-slate-800/40 backdrop-blur-xl border border-slate-700/50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="p-2 rounded-lg bg-teal-500/20">
                              <PlusCircle className="h-4 w-4 text-teal-400" />
                            </div>
                            <div>
                              <h3 className="text-lg font-bold text-white">
                                Societies
                              </h3>
                              <p className="text-slate-400 text-xs">
                                Maximum 2 societies allowed
                              </p>
                            </div>
                          </div>
                          <div className="px-3 py-1 rounded-full bg-teal-500/20 border border-teal-500/30">
                            <span className="text-teal-300 font-semibold text-sm">
                              {selectedCustomer.societies?.length || 0}/2
                            </span>
                          </div>
                        </div>

                        <div className="flex gap-3">
                          <select
                            id="societySelector"
                            value={selectedSocietyId}
                            onChange={(e) => {
                              setSelectedSocietyId(e.target.value);
                              if (e.target.value) {
                                const society = getSocietyById(e.target.value);
                                setSocietyToAdd(society);
                              }
                            }}
                            className="flex-1 px-4 py-2.5 rounded-lg bg-slate-900/60 border border-slate-700 text-white text-sm
                    focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 hover:border-slate-600
                    transition-all duration-300 backdrop-blur-sm cursor-pointer"
                          >
                            <option value="">Select a society...</option>
                            {societies
                              .filter(
                                (society) =>
                                  !selectedCustomer.societies?.some(
                                    (s) =>
                                      (typeof s === "object" ? s._id : s) ===
                                      society._id
                                  )
                              )
                              .map((society) => (
                                <option
                                  key={society._id}
                                  value={society._id}
                                  className="bg-slate-800"
                                >
                                  {society.name}
                                </option>
                              ))}
                          </select>

                          <button
                            type="button"
                            disabled={
                              !selectedSocietyId ||
                              selectedCustomer.societies?.length >= 2
                            }
                            onClick={() => {
                              if (selectedSocietyId && societyToAdd) {
                                setShowAddDialog(true);
                              }
                            }}
                            className={`px-5 py-2.5 rounded-lg flex items-center gap-2 font-semibold text-sm transition-all duration-300 ${
                              selectedSocietyId &&
                              selectedCustomer.societies?.length < 2
                                ? "bg-gradient-to-r from-teal-500 to-teal-600 text-white hover:shadow-lg hover:shadow-teal-500/50 hover:scale-105"
                                : "bg-slate-700/50 text-slate-500 cursor-not-allowed"
                            }`}
                          >
                            <PlusCircle size={16} />
                            Add
                          </button>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {selectedCustomer.societies?.map((society) => {
                            const societyName =
                              typeof society === "object"
                                ? society.name
                                : societies.find((s) => s._id === society)
                                    ?.name || society;
                            return (
                              <div
                                key={
                                  typeof society === "object"
                                    ? society._id
                                    : society
                                }
                                className="px-4 py-2 rounded-full bg-gradient-to-r from-teal-500/20 to-slate-600/20 border border-teal-500/30 
                        hover:from-teal-500/30 hover:to-slate-600/30 transition-all duration-300 flex items-center gap-2"
                              >
                                <span className="text-white font-medium text-sm">
                                  {societyName}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedCustomer((prev) => ({
                                      ...prev,
                                      societies: prev.societies.filter(
                                        (s) =>
                                          (typeof s === "object"
                                            ? s._id
                                            : s) !==
                                          (typeof society === "object"
                                            ? society._id
                                            : society)
                                      ),
                                    }));
                                  }}
                                  className="p-1 rounded-full bg-red-500/20 hover:bg-red-500/40 text-red-400 transition-all duration-300 hover:rotate-90"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            );
                          })}
                          {(!selectedCustomer.societies ||
                            selectedCustomer.societies.length === 0) && (
                            <div className="text-slate-500 italic text-sm py-1">
                              No societies selected
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Action Buttons - SMALLER */}
                      <div className="flex gap-4 pt-4">
                        <button
                          onClick={handleUpdate}
                          className="flex-1 relative group overflow-hidden rounded-xl"
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-teal-500 to-teal-600 transition-all duration-500 group-hover:from-teal-400 group-hover:to-teal-500"></div>
                          <div className="relative px-6 py-3 font-bold text-base text-white flex items-center justify-center">
                            <span>Save Changes</span>
                          </div>
                        </button>

                        <button
                          onClick={() => setModalOpen(false)}
                          className="flex-1 px-6 py-3 rounded-xl font-bold text-base text-white bg-slate-700/50 hover:bg-slate-600/50 
                  border border-slate-600 hover:border-slate-500 transition-all duration-300 hover:scale-105"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {isResetModalOpen && selectedUserForReset && (
          <div className="fixed inset-0 z-50">
            {/* Backdrop */}
            <div
              className={`absolute inset-0 ${theme.tailwind.bg53} backdrop-blur-sm -mt-14`}
            />
            {/* Modal Container */}
            <div className="relative h-screen overflow-y-auto">
              <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
                <div
                  className={`relative ${theme.tailwind.bgGradient20} 
        rounded-2xl border border-white/10 shadow-2xl w-96 p-8 space-y-6`}
                >
                  <div className="flex justify-between items-center">
                    <h2
                      className={`text-2xl font-bold ${theme.tailwind.text9}`}
                    >
                      Reset Password
                    </h2>
                    <button
                      onClick={() => setResetModalOpen(false)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <XCircle className="w-6 h-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div className="relative">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        autoComplete="new-password"
                        placeholder="New Password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className={`w-full p-3 pr-12 rounded-xl bg-white/5 border border-white/10 
                ${theme.tailwind.text9} ${theme.tailwind.placeHolder} ${theme.tailwind.focus}`}
                      />
                      <button
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2"
                      >
                        {showNewPassword ? (
                          <EyeOff className={`${theme.tailwind.text9}`} />
                        ) : (
                          <Eye className={`${theme.tailwind.text9}`} />
                        )}
                      </button>
                    </div>

                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        autoComplete="new-password"
                        placeholder="Confirm Password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className={`w-full p-3 pr-12 rounded-xl bg-white/5 border border-white/10 
                ${theme.tailwind.text9} ${theme.tailwind.placeHolder} ${theme.tailwind.focus}`}
                      />
                      <button
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        className="absolute right-3 top-1/2 -translate-y-1/2"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className={`${theme.tailwind.text9}`} />
                        ) : (
                          <Eye className={`${theme.tailwind.text9}`} />
                        )}
                      </button>
                    </div>

                    {/* Password Strength Indicator */}
                    <div
                      className={`h-2 w-full ${theme.tailwind.bg26} rounded-full overflow-hidden`}
                    >
                      <div
                        className={`h-full transition-all duration-300 ${
                          getPasswordStrength(newPassword) === "Weak"
                            ? "bg-red-500 w-1/3"
                            : getPasswordStrength(newPassword) === "Medium"
                            ? "bg-yellow-500 w-2/3"
                            : "bg-green-500 w-full"
                        }`}
                      />
                    </div>
                    <div className={`text-sm ${theme.tailwind.text4}`}>
                      Password Strength: {getPasswordStrength(newPassword)}
                    </div>
                  </div>

                  <button
                    onClick={handleResetPassword}
                    disabled={
                      newPassword !== confirmPassword || newPassword.length < 6
                    }
                    className={`w-full py-3 rounded-xl transition-all duration-300 ${
                      newPassword !== confirmPassword || newPassword.length < 6
                        ? `${theme.tailwind.bg19} ${theme.tailwind.text19} cursor-not-allowed`
                        : `${theme.tailwind.bgr28} ${theme.tailwind.text9} hover:scale-[1.02]`
                    }`}
                  >
                    Reset Password
                  </button>
                </div>
              </div>
            </div>{" "}
          </div>
        )}
      </div>
      {/* Headless UI Dialog for Waris Info */}
      <Dialog
        open={isWarisModalOpen}
        onClose={() => setWarisModalOpen(false)}
        className="relative z-50"
      >
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm"
          aria-hidden="true"
        />

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel
            className={`w-full max-w-2xl transform overflow-hidden rounded-2xl ${gradient} p-6 text-left align-middle shadow-xl transition-all border ${theme.tailwind.border1}`}
          >
            <div className="flex justify-between items-center mb-6">
              <Dialog.Title
                className={`text-2xl font-bold ${theme.tailwind.bgr22} text-transparent bg-clip-text`}
              >
                Waris Information
              </Dialog.Title>
              <button
                onClick={() => setWarisModalOpen(false)}
                className={`${theme.tailwind.text20} ${theme.tailwind.hover1} transition-colors`}
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {[
                { label: "Name", value: selectedWaris?.waris_name, icon: User },
                {
                  label: "Relation",
                  value: selectedWaris?.waris_relation,
                  icon: UserPlus,
                },
                {
                  label: "Phone",
                  value: selectedWaris?.waris_phone,
                  icon: Phone,
                },
                {
                  label: "CNIC",
                  value: selectedWaris?.waris_cnic,
                  icon: IdCard,
                },
                {
                  label: "Father's Name",
                  value: selectedWaris?.waris_father_name,
                  icon: User,
                },
                {
                  label: "Address",
                  value: selectedWaris?.waris_address,
                  icon: NotebookTabs,
                },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className={`flex items-center gap-4 p-3 rounded-xl ${theme.tailwind.bgr24} group ${theme.tailwind.groupHoverG7} transition-all duration-300`}
                >
                  <div
                    className={`p-2 rounded-lg ${theme.tailwind.bg11} ${theme.tailwind.groupHoverBg1} transition-colors`}
                  >
                    <item.icon className={`h-5 w-5 ${theme.tailwind.text9}`} />
                  </div>
                  <div>
                    <p className={`text-sm ${theme.tailwind.text20}`}>
                      {item.label}
                    </p>
                    <p className={`${theme.tailwind.text9} font-medium`}>
                      {item.value || "N/A"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
};

export default CustomerTable;
